import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { FaMapMarkerAlt, FaPhone, FaClock } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send with placeholder values for required backend fields
      const payload = {
        ...formData,
        vehicle_model: 'Not specified',
        service_required: 'General Enquiry'
      };
      const response = await axios.post(`${BACKEND_URL}/api/contact`, payload);
      if (response.data.status === 'success') {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center bg-cover bg-center"
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/fyzgh57f_Workshop3.jpeg)' }}
      >
        <div className="absolute inset-0 bg-dark-navy/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full text-center">
          <h1 className="font-cabinet font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-lg text-gray-200">{t('contact.subheading')}</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="font-cabinet font-bold text-2xl sm:text-3xl text-dark-navy mb-8">
                {t('contact.heading')}
              </h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-royal-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <FaMapMarkerAlt className="text-royal-blue" size={20} />
                  </div>
                  <div>
                    <h3 className="font-cabinet font-semibold text-lg text-dark-navy mb-2">
                      {t('contact.address')}
                    </h3>
                    <p className="text-gray-600 text-base">
                      Lot 3663, Batu 1 3/4<br />
                      Jalan Bakri<br />
                      84000 Muar Johor
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-royal-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <FaPhone className="text-royal-blue" size={20} />
                  </div>
                  <div>
                    <h3 className="font-cabinet font-semibold text-lg text-dark-navy mb-2">
                      {t('contact.phone')}
                    </h3>
                    <a href="tel:+6069513317" className="text-gray-600 text-base hover:text-royal-blue transition-colors">
                      +606-951 3317
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-royal-blue/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <FaClock className="text-royal-blue" size={20} />
                  </div>
                  <div>
                    <h3 className="font-cabinet font-semibold text-lg text-dark-navy mb-2">
                      {t('contact.hours')}
                    </h3>
                    <p className="text-gray-600 text-base">
                      {t('contact.hoursValue')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="w-full h-[400px] border border-gray-200" data-testid="google-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3984.3456!2d102.5675!3d2.0444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMsKwMDInNDAuMCJOIDEwMsKwMzQnMDMuMCJF!5e0!3m2!1sen!2smy!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Leng Kee Auto Service Centre Location"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-light-grey p-8 border border-gray-200">
                <h2 className="font-cabinet font-bold text-2xl text-dark-navy mb-6">
                  {t('contact.formTitle')}
                </h2>
                
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-dark-navy font-medium">
                        {t('contact.name')} *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="input-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-dark-navy font-medium">
                        {t('contact.phoneLabel')} *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-dark-navy font-medium">
                        {t('contact.message')}
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="input-message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className="mt-2"
                        placeholder="Tell us about your vehicle and how we can help"
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="submit-contact-form"
                      disabled={isSubmitting}
                      className="w-full bg-royal-blue hover:bg-[#002277] text-white font-cabinet font-bold py-6"
                    >
                      {isSubmitting ? t('contact.submitting') : t('contact.submit')}
                    </Button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 text-green-800 text-sm" data-testid="success-message">
                        {t('contact.success')}
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="error-message">
                        {t('contact.error')}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;